// The one way a dimension is drawn, everywhere.
//
// Before this, the report card, the reveal chart, the compact dashboard list, the
// fingerprint grid and the dashboard tile each computed their own bar. They
// disagreed with each other, and every one of them drew a weighted dimension as a
// single bar whose length was the top option's share — so a genuine three-way
// split (visual 34 · text 33 · spoken 33) rendered as "Visual — low".
//
// Three shapes, three pictures:
//   weighted  → a segmented bar showing every option's share, largest first
//   scalar    → one bar with a number, because a position on a scale IS a number
//   axes      → two rails, one marker each, poles named at both ends
//
// Bars show WHERE a learner sits, not how well they did. There is no good end of
// a preference scale — a low depth tolerance is not worse than a high one, and the
// Viewpoint Range spec is explicit that both ends are legitimate. So there is no
// green-to-red, and no "high = gold" by magnitude. Each dimension keeps its own
// identity colour; the only emphasis is for a CLEAR result (one option really did
// dominate), which is a fact about the evidence, not a grade.
//
// JB asked (2026-09-05) for "different colours based on % and also show
// percentage". Percentages are shown wherever they mean something: shares on the
// segmented bar, a 0–100 position on a scalar bar. Colour-by-magnitude was
// deliberately not done, for the reason above — the decision note lives here and
// in the report card caption so it is his to overturn.

import type { DimensionDisplayData } from '../dashboard/dimensionHelpers';

interface DimensionMeterProps {
  dim: DimensionDisplayData;
  /** compact = 2.5px rails, no legend. full = 6px rails with legend and numbers. */
  size?: 'compact' | 'full';
  /** Animate the fill in. Off for FLIP-animated contexts that manage their own. */
  animate?: boolean;
  /** Stagger delay in ms. */
  delay?: number;
}

const TRACK = 'bg-dark-subtle';

export function DimensionMeter({ dim, size = 'full', animate = true, delay = 0 }: DimensionMeterProps) {
  const h = size === 'compact' ? 'h-[3px]' : 'h-1.5';
  const transition = animate ? `width 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms` : 'none';

  if (dim.locked || !dim.assessed || dim.kind === 'perUpload') {
    // Nothing to draw. A filled bar is a claim, and there is nothing to claim.
    return <div className={`w-full ${h} rounded-pill ${TRACK}`} />;
  }

  if (dim.kind === 'weighted' && dim.segments) {
    const legend = dim.segments.filter((s) => s.share > 0);
    return (
      <div>
        <div className={`w-full ${h} rounded-pill ${TRACK} overflow-hidden flex`}>
          {dim.segments.map((seg, i) => (
            <div
              key={seg.key}
              title={`${seg.label} ${seg.share}%`}
              className="h-full"
              style={{
                width: `${seg.share}%`,
                background: seg.colour,
                transition,
                // Hairline between segments so adjacent shades read as separate.
                borderRight: i < dim.segments!.length - 1 && seg.share > 0 ? '1px solid rgba(0,0,0,0.35)' : 'none',
              }}
            />
          ))}
        </div>
        {size === 'full' && (
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {legend.map((seg) => (
              <span key={seg.key} className="inline-flex items-center gap-1 text-[11px] text-content-muted font-body">
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: seg.colour }} />
                {seg.label} <span className="font-code text-content-dim">{seg.share}%</span>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (dim.kind === 'axes' && dim.axes) {
    return (
      <div className={size === 'compact' ? 'space-y-1' : 'space-y-2'}>
        {dim.axes.map((axis) => (
          <div key={axis.key}>
            {size === 'full' && (
              <div className="flex justify-between text-[10.5px] text-content-dim font-body mb-0.5">
                <span>{axis.lowPole}</span>
                <span>{axis.highPole}</span>
              </div>
            )}
            <div className={`relative w-full ${h} rounded-pill ${TRACK}`}>
              {/* Centre tick: the balanced zone lives either side of it. */}
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-dark-border" />
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full"
                title={`${axis.value} / 100${axis.balanced ? ' — balanced' : ''}`}
                style={{
                  left: `${axis.value}%`,
                  width: size === 'compact' ? 7 : 11,
                  height: size === 'compact' ? 7 : 11,
                  background: axis.balanced ? 'transparent' : dim.color,
                  border: `2px solid ${dim.color}`,
                  transition: animate ? `left 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms` : 'none',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // scalar
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 ${h} rounded-pill ${TRACK} overflow-hidden`}>
        <div
          className="h-full rounded-pill"
          style={{ width: `${dim.value}%`, background: dim.color, transition }}
        />
      </div>
      {size === 'full' && dim.showNumber && (
        <span className="font-code text-[11px] text-content-muted tabular-nums w-9 text-right">
          {Math.round(dim.value)}<span className="text-content-dim">/100</span>
        </span>
      )}
    </div>
  );
}

/** The one-line caption that tells a learner how to read the bars. */
export const METER_CAPTION =
  'These show where you sit, not how well you did — a preference scale has no good end.';
